import dayjs from "dayjs";
import Loader from "./Loader";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useState, useEffect } from "react";
import Collapse from "@mui/material/Collapse";
import VideoModal from "../Modals/VideoModal";
import { Theme } from "../../data/style/Theme";
import { useAuth } from "../../context/AuthContext";
import SearchIcon from "@mui/icons-material/Search";
import { useMutation } from "@tanstack/react-query";
import { getExerciseProgress } from "../../api/user";
import { errorTexts } from "../../data/assets/texts";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import { useNotifications } from "../../context/NotificationContext";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  MainText,
  SpacedRow,
  ExerciseProgress,
  AllCenteredColumn,
  SecondaryTitle,
} from "../../data/style/Style";

const ExerciseEntity = ({ item, showExerciseDetails, type }) => {
  const { settings } = useAuth();
  const { enableNotification } = useNotifications();
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [videoModalActive, setVideoModalActive] = useState(false);
  const [video, setVideo] = useState(null);
  const [exerciseProgress, setExerciseProgress] = useState([]);
  const weightUnit = settings.weight === 0 ? "kg" : "lb";

  const unit =
    type === "cardio" ? "min" : type === "gymnastics" ? "reps" : weightUnit;
  const color =
    type === "cardio"
      ? Theme.palette.up
      : type === "gymnastics"
        ? Theme.palette.bronze
        : Theme.palette.silver;

  const valueField =
    type === "weightlifting" && settings.weight === 1
      ? "weight_lb"
      : type === "cardio"
        ? "converted_score"
        : "value";

  const valueHeader =
    type === "cardio" ? "Time" : type === "gymnastics" ? "Reps" : "Weight";

  const { mutate, isSuccess } = useMutation({
    mutationFn: getExerciseProgress,
    onSuccess: (response) => setExerciseProgress(response.data.progress),
    onError: (error) => {
      if (error.response.status === 400)
        enableNotification(error.response.data.message, "error");
      if (error.response.status >= 500)
        enableNotification(errorTexts.system_error, "error");
    },
  });

  useEffect(() => {
    if (!detailsVisible) return;
    if (type === "cardio") {
      mutate({
        exerciseId: item.exercise,
        distance: item.distance,
        category: type,
      });
    } else {
      mutate({
        exerciseId: item.exercise,
        category: type,
      });
    }
  }, [detailsVisible]);

  return (
    <Box>
      <ExerciseProgress alignItems={"center"} gap={2} borderLeftColor={color}>
        <Box width={"35%"}>
          <MainText>
            {type === "cardio" && (
              <>
                {settings.distance === 0 ? (
                  <>
                    {item.distance > 1000
                      ? item.distance / 1000 + " km"
                      : item.distance + " m"}
                  </>
                ) : (
                  <>
                    {item.distance > 1000
                      ? item.distance_ft + " mi"
                      : item.distance_ft + " ft"}
                  </>
                )}
                &nbsp;
              </>
            )}
            {item.exercise_name}
          </MainText>
        </Box>
        <AllCenteredColumn
          p={1}
          borderRadius={3}
          sx={{
            border: 1,
            borderColor: Theme.palette.border,
            background: Theme.palette.card,
            "&:hover": {
              background: Theme.palette.surface,
              borderColor: color,
            },
          }}
          onClick={() => showExerciseDetails(item, type)}
        >
          <SearchIcon sx={{ color }} />
        </AllCenteredColumn>
        <MainText
          fontWeight={700}
          sx={{
            color: color + " !important",
          }}
        >
          {item[valueField]}
          &nbsp;
          {unit}
        </MainText>
        <MainText onClick={() => setDetailsVisible((prev) => !prev)}>
          {detailsVisible ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </MainText>
      </ExerciseProgress>
      <Collapse unmountOnExit in={detailsVisible} timeout={500}>
        <Stack py={1} gap={1}>
          <SpacedRow pl={1} gap={2}>
            <Box width={"30%"}>
              <SecondaryTitle>Session</SecondaryTitle>
            </Box>
            <SecondaryTitle>{valueHeader}</SecondaryTitle>
            <SecondaryTitle>Video</SecondaryTitle>
          </SpacedRow>
          {isSuccess ? (
            <>
              {exerciseProgress.map((item) => (
                <SpacedRow
                  key={item.id}
                  px={1}
                  sx={{ borderBottom: 1, borderColor: Theme.palette.border }}
                >
                  <Box width={"30%"}>
                    <MainText fontSize={"0.8rem"}>
                      {dayjs(item.created_at).format("DD MMM YYYY")}
                    </MainText>
                  </Box>
                  <MainText
                    fontSize={"0.8rem"}
                    fontWeight={700}
                    sx={{ color: color + " !important" }}
                  >
                    {item[valueField]}
                    &nbsp;
                    {unit}
                  </MainText>
                  <Box width={"5%"}>
                    {item.file_id !== 0 && (
                      <OndemandVideoIcon
                        sx={{
                          color,
                          fontSize: "1.1rem",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setVideo(item.video);
                          setVideoModalActive(true);
                        }}
                      />
                    )}
                  </Box>
                </SpacedRow>
              ))}
            </>
          ) : (
            <Loader />
          )}
        </Stack>
      </Collapse>
      <VideoModal
        active={videoModalActive}
        setActive={setVideoModalActive}
        video={video}
      />
    </Box>
  );
};

export default ExerciseEntity;
